'use client';

import { Image, trackEvent, FloatingWhatsApp, SupportedLocale } from "@habacuscode/habacus_ui_component_library";
import { initAnalytics, GoogleAnalyticsProvider, LocaleProvider } from '@habacuscode/habacus_ui_component_library';
import habacusBlue from "@/assets/images/logos/habacus_blue.svg";

import { usePathname } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { getFirebaseClientAuth } from "@/lib/firebase-client-config";
import { signInAnonymously } from "firebase/auth";
import { useMessages, useTranslations } from 'next-intl';
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Info, WizardConfig } from "@/types/configuration";
import LeftColumn from "@/components/LeftColumn";
import ClientWizardLayout from "./wizard";
import { getDateTimeCache } from "@/utils/helpers";
import { getSessionStorageData, setSessionStorageData } from "@/utils/client/session_storage_utils";
import { WHATSAPP_NUMBER } from "@/utils/constants";
import { retrieveParternshipLogo } from "@/utils/client/customizations";

export default function ClientTenantLayout({
    wizardConfig,
    locale,
    tenant,
    version,
    appEnv,
    totalSteps,
    children,
    habacusStaticUrl
}: {
    wizardConfig: WizardConfig;
    locale: SupportedLocale;
    tenant: string;
    version: string;
    appEnv: string;
    totalSteps: number;
    children: React.ReactNode;
    habacusStaticUrl: string;
}) {
    const t = useTranslations('Privacy');
    const tWA = useTranslations('WhatsApp');
    const messages = useMessages();

    const [isResultPage, setIsResultPage] = useState<boolean>(false)
    const [currentStep, setCurrentStep] = useState<string>('')
    const [currentStepInfo, setCurrentStepInfo] = useState<Info | null>(null)

    const [dateTimeCache, setDateTimeCache] = useState<string | null>(null);

    const analyticsInitializedRef = useRef(false);
    const formStartTrackedRef = useRef(false);

    const pathname = usePathname();

    useEffect(() => {
        const initAuthAndAnalytics = async () => {
            if (analyticsInitializedRef.current) {
                return;
            }

            try {

                const firebaseClientAuth = await getFirebaseClientAuth();
                if (!firebaseClientAuth) {
                    console.error('firebaseClientAuth is null');
                    return;
                }

                let user = firebaseClientAuth.currentUser;
                if (!user) {
                    const anonymousUserCredentials = await signInAnonymously(firebaseClientAuth);
                    user = anonymousUserCredentials.user;
                }

                if (!user) {
                    console.error('Impossibile ottenere un utente Firebase anonimo');
                    return;
                }

                // Inizializzo Analytics una sola volta per runtime del client
                initAnalytics(GoogleAnalyticsProvider, {
                    firebaseCurrentUserId: user.uid,
                    firebaseCurrentUserIsAnonymous: user.isAnonymous,
                });

                analyticsInitializedRef.current = true;

                // Se siamo al primo step "vero" del form e non in result page, traccio form_start una sola volta
                const stepKeys = Object.keys(wizardConfig.steps).map(Number);
                const firstStep = Math.min(...stepKeys);
                const numericStep = parseInt(currentStep || `${firstStep}`);

                if (!isResultPage && numericStep === firstStep && !formStartTrackedRef.current) {
                    trackEvent('form_start', {
                        firebase_current_user_id: user.uid,
                        firebase_current_user_status: 'anonymous',
                    });
                    formStartTrackedRef.current = true;
                }

                // Esegui solo al primo step numerico del wizard
                if (parseInt(currentStep) === firstStep) {

                    const queryParams = new URLSearchParams(window.location.search);

                    const queryObj: Record<string, string> = {};
                    queryParams.forEach((value, key) => {
                        queryObj[key] = value;
                    });

                    const allData = {
                        ...getSessionStorageData(tenant, version),
                        queryParams: queryObj,
                    };

                    setSessionStorageData(tenant, version, allData);
                }
            } catch (error) {
                console.error('Error during Firebase anonymous auth / analytics init', error);
            }
        };

        initAuthAndAnalytics();
    }, [wizardConfig, currentStep, isResultPage]);

    useEffect(() => {
        setDateTimeCache(getDateTimeCache('HH'));
    }, []);

    useEffect(() => {
        const handleBeforeUnload = () => {
            trackEvent('exit_page', {
                event_category: 'engagement',
                event_label: window?.location.pathname,
            });
        };

        const handleMouseOut = (e: MouseEvent) => {
            if (e.clientY < 0) {
                trackEvent('exit_intent', {
                    event_category: 'engagement',
                    event_label: window?.location.pathname,
                });
            }
        };

        window?.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('mouseout', handleMouseOut);

        return () => {
            window?.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('mouseout', handleMouseOut);
        };
    }, []);

    const paths = pathname.split("/");
    const step = paths[5];

    useEffect(() => {
        console.log('*** pathname changed', pathname)
        const paths = pathname.split("/");
        setCurrentStep(paths[paths.length - 1]);

        let infoConfig: Info | null = null;
        if (pathname.indexOf('/intro') !== -1) {
            const firstStep = Math.min(...Object.keys(wizardConfig.steps).map(Number));
            setCurrentStep(firstStep.toString());
        } else {
            console.log('*** wizardConfig', wizardConfig, wizardConfig.steps[step])
            const isResultPage: boolean = pathname.indexOf('/result') !== -1;
            setIsResultPage(isResultPage)
            if (isResultPage) {
                infoConfig = wizardConfig.resultPage?.info ?? null;
            } else if (wizardConfig.steps[step] && wizardConfig.steps[step].hasOwnProperty("info")) {
                console.log('*** wizardConfig step config', wizardConfig.steps[step].info)
                infoConfig = wizardConfig.steps[step].info ?? null;
            }
        }

        setCurrentStepInfo(infoConfig);
    }, [pathname])

    const isRomeWorkingHours = (): boolean => {
        const now = new Date();

        // formatter per ottenere data/ora in timezone Europa/Roma
        const formatter = new Intl.DateTimeFormat("en-GB", {
            timeZone: "Europe/Rome",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });

        const parts = formatter.formatToParts(now);
        const get = (type: string) => parts.find(p => p.type === type)?.value || "0";

        // ricostruisco la data come stringa ISO
        const romeDate = new Date(
            `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}`
        );

        const day = romeDate.getDay();   // 1 = lunedì, ... 5 = venerdì
        const hour = romeDate.getHours(); // ora locale di Roma

        return day >= 1 && day <= 5 && hour >= 9 && hour < 18;
    }

    const getStatusMessage = () => {
        if (isRomeWorkingHours()) {
            return tWA('statusMessageInWorkHour');
        } else {
            return tWA('statusMessageNotInWorkHour');
        }
    }

    return (
        <LocaleProvider locale={locale} messages={messages}>
            {!isResultPage && (
                <FloatingWhatsApp
                    key={"floatingWhatsAppComponent"}
                    phoneNumber={WHATSAPP_NUMBER}
                    accountName="Team Habacus"
                    statusMessage={getStatusMessage()}
                    chatMessages={tWA.raw('chatMessages') as string[]}
                    placeholder={tWA('placeholder')}
                    floatingMessage={tWA('floatingMessage')}
                    style={{ zIndex: "99999" }}
                    floatingMessageDelayEnter={2}
                    floatingMessageDelayExit={15}
                    styleBottom={20}
                />
            )}
            <div key={"tenantContainer"} className='layout-container h-screen flex flex-col md:flex-col lg:flex-row md:w-full'>
                {/* col 1: show on the left on desktop, second on mobile below main content of col 2   */}
                <LeftColumn
                    wizardConfig={wizardConfig}
                    isResultPage={isResultPage}
                    currentStepInfo={currentStepInfo}
                    locale={locale}
                    appEnv={appEnv}
                    tPrivacy={t}
                    habacusStaticUrl={habacusStaticUrl}
                />

                {/* col 2: show on the right on desktop, first on mobile  */}
                <div className='wizard-main-container-boxes right-column relative lg:overflow-y-auto sm:overflow-y-visible'>
                    <div className="flex flex-1 flex-col justify-center">
                        {/* Logo visible here only for mobile */}
                        <div>
                            <div className={`flex flex-row text-navy-900 text-6xl font-bold mb-0 lg:hidden pl-4 py-2 h-[60px] ${wizardConfig.habacus?.top?.mode == "dark" ? 'text-white' : ''} `}
                                style={{
                                    backgroundColor: wizardConfig.habacus?.top?.color,
                                }}>
                                <div className="flex">
                                    <Image
                                        className="object-contain self-center max-w-[140px] max-h-[48px]"
                                        showImageTitle={false}
                                        image={wizardConfig.habacus?.logo ? `${wizardConfig.habacus.logo}?ts=${dateTimeCache}` : habacusBlue.src}
                                        alt='Habacus'
                                        link='#'
                                        width={"140px"}
                                        height={"48px"}
                                    />
                                </div>
                                {wizardConfig.parternship && (
                                    <>
                                        <div className={`mx-3 self-center text-center text-sm font-bold ${wizardConfig.habacus?.left?.mode == "dark" ? 'text-white' : ''}`}>x</div>
                                        <div className="self-center h-full flex align-middle">
                                            {retrieveParternshipLogo(wizardConfig.parternship, dateTimeCache)}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="justify-end items-end flex md:hidden absolute right-4 top-0 xs:top-2">
                                <LanguageSwitcher />
                            </div>
                        </div>
                        <ClientWizardLayout
                            locale={locale}
                            tenant={tenant}
                            version={version}
                            totalSteps={totalSteps}
                        >
                            {children}
                        </ClientWizardLayout>
                    </div>
                </div>
            </div>
        </LocaleProvider>
    );
}
